import { useEffect, useState, useCallback, useRef } from "react";
import { Bell, Check, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * Campanella notifiche in-app.
 * Fa polling ogni 30s. Mostra badge con conteggio non lette.
 * Click aperto → popover con lista + "Marca tutte come lette".
 */
export default function NotificationBell({ leagueId }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = useCallback(async () => {
    if (!leagueId) return;
    try {
      const r = await api.get(`/leagues/${leagueId}/notifications`, { params: { limit: 20 } });
      setItems(r.data.items || []);
      setUnread(r.data.unread_count || 0);
    } catch {
      // silent
    }
  }, [leagueId]);

  useEffect(() => {
    load();
    const t = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, 30000);
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const readOne = async (id) => {
    try {
      await api.post(`/leagues/${leagueId}/notifications/${id}/read`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const readAll = async () => {
    try {
      await api.post(`/leagues/${leagueId}/notifications/read-all`);
      toast.success("✅ Tutte lette");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const fmtTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return "ora";
    if (diff < 3600) return `${Math.floor(diff / 60)}m fa`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h fa`;
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(v => !v)}
        data-testid="notification-bell"
        className={`relative p-2 rounded-2xl border transition-all ${
          unread > 0 ? "border-amber-500/50 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 shadow-md shadow-amber-500/20 animate-pulse-slow" : "border-slate-700 text-slate-400 hover:text-white hover:border-slate-500"
        }`}
        title={`${unread} notifiche non lette`}
      >
        <Bell size={16} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-black flex items-center justify-center border-2 border-slate-900" data-testid="notification-count">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 rounded-3xl border border-slate-800 bg-gradient-to-br from-slate-900 to-slate-950 shadow-2xl shadow-black/70 backdrop-blur-md z-50 overflow-hidden" data-testid="notification-popover">
          <div className="flex items-center justify-between p-3 border-b border-slate-800">
            <div className="font-black uppercase text-sm tracking-wider flex items-center gap-2">
              🔔 Notifiche {unread > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40">{unread} nuove</span>}
            </div>
            {unread > 0 && (
              <button onClick={readAll} data-testid="notification-read-all"
                className="text-[10px] text-emerald-300 hover:text-emerald-200 uppercase tracking-wider font-bold flex items-center gap-1">
                <Check size={11}/> Segna tutte
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto divide-y divide-slate-800">
            {items.length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-sm">
                <div className="text-3xl mb-2">📭</div>
                Nessuna notifica.
              </div>
            ) : (
              items.map(n => (
                <div key={n.notification_id}
                  className={`p-3 flex items-start gap-3 hover:bg-slate-800/40 transition-colors ${!n.read ? "bg-amber-500/5 border-l-2 border-l-amber-400" : ""}`}
                  data-testid={`notification-item-${n.notification_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm ${!n.read ? "font-bold text-white" : "text-slate-400"}`}>{n.message}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{fmtTime(n.created_at)}</div>
                  </div>
                  {!n.read && (
                    <button onClick={() => readOne(n.notification_id)} data-testid={`notification-read-${n.notification_id}`}
                      className="text-slate-500 hover:text-emerald-300 p-1" title="Segna come letta">
                      <Check size={13}/>
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
